<template>
  <a-modal :visible="visible" title="订单详情" width="900px" :footer="null" @cancel="handleClose" destroyOnClose>
    <a-spin :spinning="loading">
      <a-descriptions bordered :column="2" size="small" class="order-desc">
        <a-descriptions-item label="订单号">
          <span style="font-weight: bold; color: #333;">{{ detail.orderNo || '--' }}</span>
        </a-descriptions-item>
        <a-descriptions-item label="客户ID">
          {{ detail.customerId || '--' }}
        </a-descriptions-item>
        <a-descriptions-item label="应付金额">
          <span style="font-weight: bold;">￥{{ detail.amount }}</span>
        </a-descriptions-item>
        <a-descriptions-item label="实付金额">
          <span style="color: #ff4d4f; font-weight: bold;">￥{{ detail.payAmount }}</span>
        </a-descriptions-item>
        <a-descriptions-item label="状态">
          <a-tag :color="getStatusColor(detail.status)">
            {{ detail.status || '--' }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="来源">
          {{ detail.source || '--' }}
        </a-descriptions-item>
        <a-descriptions-item label="创建时间">
          {{ detail.createdDate || '--' }}
        </a-descriptions-item>
        <a-descriptions-item label="支付时间">
          {{ detail.payDate || '--' }}
        </a-descriptions-item>

        <a-descriptions-item v-if="detail.afterServiceType" label="售后类型">
          <a-tag color="orange">{{ detail.afterServiceType }}</a-tag>
        </a-descriptions-item>

        <a-descriptions-item label="收货信息" :span="2">
          <div v-if="detail.express">
            <div>
              <span style="font-weight: bold; margin-right: 10px;">{{ detail.express.receiverName }}</span>
              <span>{{ detail.express.receiverPhone }}</span>
            </div>
            <div style="color: #666; font-size: 12px; margin-top: 4px;">
              {{ formatAddress(detail.express) }}
            </div>
          </div>
          <span v-else style="color: #999;">无收货信息</span>
        </a-descriptions-item>

        <a-descriptions-item label="客户留言" :span="2">
          {{ detail.customerComment || '无' }}
        </a-descriptions-item>
      </a-descriptions>

      <div class="section-title">商品清单</div>
      <a-table class="ant-table-striped" :columns="productColumns" :data-source="detail.items || []" :pagination="false" row-key="productId" size="small" bordered>
        <template #price="{ text }">
          ￥{{ Number(text).toFixed(2) }}
        </template>
        <template #total="{ text }">
          <span style="color: #ff4d4f;">￥{{ Number(text).toFixed(2) }}</span>
        </template>
        <template #productName="{ text }">
          <a-space>
            <span style="font-weight: 500;">{{ text }}</span>
          </a-space>
        </template>
      </a-table>

      <div class="footer-area">
        <div class="timeline-area">
          <div v-if="operationHistory.length === 0" style="color: #999; padding: 10px;">暂无操作记录</div>
          <div v-else class="inner-title">操作记录</div>
          <a-timeline>
            <a-timeline-item v-for="item in operationHistory" :key="item.id" :color="getTimelineColor(item)">
              <div class="timeline-header">
                <span class="op-date">{{ item.opDate }}</span>
                <span class="op-name">{{ item.operation || '订单状态更新' }}</span>
              </div>
              <div class="op-detail">
                <span v-if="item.oldStatus">
                  {{ item.oldStatus }}
                  <arrow-right-outlined class="arrow-icon" />
                </span>
                <span class="status-highlight">{{ item.newStatus }}</span>
              </div>
            </a-timeline-item>
          </a-timeline>
        </div>

        <div class="action-area">
          <div class="status-display">
            <span class="label">当前状态：</span>
            <span class="value" :style="{ color: getStatusColor(detail.status) }">
              {{ detail.status || '--' }}
            </span>
          </div>
          <a-space>
            <a-button v-if="canEditAddress" type="primary" ghost @click="handleEditAddress">
              修改地址
            </a-button>

            <template v-for="opName in displayAvailableOps" :key="opName">
              <a-popconfirm v-if="actionConfig[opName] && actionConfig[opName].confirm" :title="actionConfig[opName].confirmMsg" ok-text="确定" cancel-text="取消" @confirm="triggerAction(opName)">
                <a-button :type="actionConfig[opName].type" :ghost="actionConfig[opName].ghost" :danger="actionConfig[opName].danger">
                  {{ opName }}
                </a-button>
              </a-popconfirm>

              <a-button v-else :type="actionConfig[opName] ? actionConfig[opName].type : 'default'" :ghost="actionConfig[opName] ? actionConfig[opName].ghost : false" :danger="actionConfig[opName] ? actionConfig[opName].danger : false" @click="triggerAction(opName)">
                {{ opName }}
              </a-button>
            </template>
          </a-space>
        </div>
      </div>
    </a-spin>

    <ShipModal v-model:visible="shipVisible" :detail="detail" @refresh="fetchData" />
    <AddressEditModal ref="addressModal" v-model:visible="addressVisible" :order-id="detail?.id" @refresh="fetchData" />
    <RefundModal v-model:visible="refundVisible" :detail="detail" @refresh="fetchData" />
    <AfterSalesModal v-model:visible="afterSalesVisible" :detail="detail" :op-name="currentOpName" @refresh="fetchData" />

  </a-modal>
</template>

<script>

import {
  GetOrderDetail,
  GetOperationHistory,
  GetAvailableOperations,
  ExecuteOperation,
  GetAfterSalesOperations,
  ExecuteAfterSalesOperation
} from '@/api/order';

import ShipModal from '@/components/order/ShipModal.vue';
import AddressEditModal from '@/components/order/AddressEditModal.vue';
import RefundModal from '@/components/order/RefundModal.vue';
import AfterSalesModal from '@/components/order/AfterSalesModal.vue';
import { ArrowRightOutlined } from '@ant-design/icons-vue';

export default {
  name: 'OrderDetail',
  components: {
    ShipModal,
    AddressEditModal,
    RefundModal,
    AfterSalesModal,
    ArrowRightOutlined
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    uuid: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      shipVisible: false,
      addressVisible: false,
      refundVisible: false,
      afterSalesVisible: false,

      detail: {},
      operationHistory: [],
      orderAvailableOps: [], // 存储常规订单可用操作
      afterSalesAvailableOps: [], // 存储售后可用操作
      currentOpName: '',

      productColumns: [
        { title: '商品名称', dataIndex: 'productName', slots: { customRender: 'productName' } },
        { title: '单价', dataIndex: 'price', width: 120, slots: { customRender: 'price' } },
        { title: '数量', dataIndex: 'quantity', width: 100, align: 'center' },
        { title: '小计', dataIndex: 'total', width: 120, slots: { customRender: 'total' } }
      ],

      actionConfig: {
        // === 基础操作 ===
        '取消订单': { type: 'danger', ghost: false, confirm: true, confirmMsg: '确定要取消该订单吗？此操作不可恢复。' },
        '发货': { type: 'primary', ghost: false, confirm: false, isModal: true },
        '退款': { type: 'default', danger: true, confirm: false, isModal: true },
        '确认收货': { type: 'primary', ghost: true, confirm: true, confirmMsg: '确认已收到货物且无误？' },
        '取消发货': { type: 'danger', ghost: true, confirm: true, confirmMsg: '确定要撤销发货状态吗？' },

        // === 售后相关操作 ===
        '申请售后': { type: 'primary', ghost: true, isAfterSales: true },
        '客户取消售后': { type: 'default', danger: false, confirm: true, confirmMsg: '确定要取消售后申请吗？' },
        '售后审核通过': { type: 'primary', ghost: false, isAfterSales: true },
        '售后审核拒绝': { type: 'danger', ghost: true, isAfterSales: true },
        '售后审核': { type: 'primary', ghost: false, isAfterSales: true }
      }
    };
  },
  computed: {
    canEditAddress() {
      const status = this.detail?.status;
      const forbiddenStats = ['已发货', '已完成', '已取消', '待支付'];
      return status === '已支付' || (status && !forbiddenStats.includes(status));
    },

    // 处理按钮合并显示的逻辑 (合并订单操作与售后操作)
    displayAvailableOps() {
      // 合并两端操作并去重
      let ops = Array.from(new Set([...this.orderAvailableOps, ...this.afterSalesAvailableOps]));

      const hasPass = ops.includes('售后审核通过');
      const hasReject = ops.includes('售后审核拒绝');

      // 如果存在审核按钮，则合并显示为"售后审核"
      if (hasPass || hasReject) {
        ops = ops.filter(op => op !== '售后审核通过' && op !== '售后审核拒绝');
        if (!ops.includes('售后审核')) {
          ops.push('售后审核');
        }
      }
      return ops;
    }
  },
  watch: {
    visible(val) {
      if (val && this.uuid) {
        this.fetchData();
      } else {
        this.resetData();
      }
    }
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const detailRes = await GetOrderDetail({ uuid: this.uuid });
        if (detailRes.errCode !== 0 || !detailRes?.data) {
          throw new Error(detailRes.message || '获取订单详情失败');
        }
        this.detail = detailRes.data;

        const orderId = this.detail?.id;
        if (!orderId) return;

        // 根据配图要求，获取列表的参数是 id: 订单编号
        const params = { id: orderId };

        // 并发获取：操作历史记录、订单可用操作、售后可用操作
        const [historyRes, opsRes, afterOpsRes] = await Promise.all([
          GetOperationHistory(params),
          GetAvailableOperations(params),
          GetAfterSalesOperations(params)
        ]);

        if (historyRes.errCode === 0) this.operationHistory = historyRes.data || [];
        if (opsRes.errCode === 0) this.orderAvailableOps = opsRes.data || [];
        if (afterOpsRes.errCode === 0) this.afterSalesAvailableOps = afterOpsRes.data || [];

      } catch (error) {
        this.$message.error(error.message || '数据加载异常');
      } finally {
        this.loading = false;
      }
    },

    handleEditAddress() {
      this.addressVisible = true;
      this.$nextTick(() => {
        // 使用可选链保证健壮性 [cite: 2]
        const expressData = this.detail?.express || null;
        const comment = this.detail?.customerComment || '';
        this.$refs.addressModal.initForm(expressData, comment);
      });
    },

    triggerAction(opName) {
      this.currentOpName = opName;
      const config = this.actionConfig[opName] || {};

      if (config.isModal || opName === '发货') {
        this.shipVisible = true;
        return;
      }

      if (opName === '退款' || opName === '发起退款') {
        this.refundVisible = true;
        return;
      }

      if (config.isAfterSales) {
        this.afterSalesVisible = true;
        return;
      }

      this.handleGenericAction(opName);
    },

    async handleGenericAction(opName) {
      this.loading = true;
      try {
        // 判断当前操作是否属于售后分支
        const isAfterSalesOp = this.afterSalesAvailableOps.includes(opName);
        const apiCall = isAfterSalesOp ? ExecuteAfterSalesOperation : ExecuteOperation;

        // 组装参数：售后操作强制使用首字母大写的 Uuid
        const params = isAfterSalesOp
          ? { uuid: this.detail?.uuid || this.detail?.id, operation: opName }
          : { id: this.detail?.id, operation: opName };

        const res = await apiCall(params);
        if (res.errCode === 0) {
          this.$message.success(`${opName} 操作成功`);
          this.fetchData();
          this.$emit('refresh');
        } else {
          this.$message.error(res.message || '操作失败');
        }
      } catch (error) {
        this.$message.error('网络异常');
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.$emit('update:visible', false);
    },

    resetData() {
      this.detail = {};
      this.operationHistory = [];
      this.orderAvailableOps = [];
      this.afterSalesAvailableOps = [];
      this.currentOpName = '';
    },

    formatAddress(express) {
      if (!express) return '';
      return [express.province, express.city, express.district, express.detailAddress]
        .filter(item => item)
        .join(' ');
    },

    getStatusColor(status) {
      const colorMap = {
        '已支付': '#52c41a',
        '待支付': '#faad14',
        '已发货': '#1890ff',
        '已取消': '#ff4d4f',
        '已完成': '#52c41a'
      };
      return colorMap[status] || '#8c8c8c';
    },

    getTimelineColor(item) {
      const op = item?.operation || '';
      const status = item?.newStatus || '';
      if (op.includes('确认') || status === '已完成' || status === '已支付') return 'green';
      if (op.includes('发货') || status === '已发货') return 'blue';
      if (op.includes('取消') || status === '已取消') return 'red';
      return 'gray';
    }
  }
};
</script>

<style scoped>
.section-title {
  margin-top: 24px;
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 16px;
  color: #1f1f1f;
  border-left: 4px solid #1890ff;
  padding-left: 10px;
}
.footer-area {
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-top: 20px;
  border-top: 1px dashed #e8e8e8;
  gap: 24px;
}
.timeline-area {
  flex: 1;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 12px;
}
.action-area {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  min-width: 200px;
}
.status-display {
  background: #f9f9f9;
  padding: 8px 16px;
  border-radius: 4px;
  display: flex;
  align-items: baseline;
}
.status-display .label {
  font-size: 14px;
  color: #8c8c8c;
}
.status-display .value {
  font-size: 20px;
  font-weight: 700;
  margin-left: 8px;
}
.timeline-header {
  margin-bottom: 4px;
}
.op-date {
  margin-right: 10px;
  color: #999;
  font-size: 12px;
}
.op-name {
  font-weight: 600;
  color: #333;
}
.op-detail {
  background: #f5f5f5;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  display: inline-flex;
  align-items: center;
}
.arrow-icon {
  margin: 0 6px;
  font-size: 10px;
  color: #bfbfbf;
}
.status-highlight {
  color: #1890ff;
  font-weight: 500;
}
</style>